import { flux } from '@/core/flux.module';
import { errorToast } from '@/utilities/toast.utilities';
import type { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import { sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { TableColumns } from '@/vantage/types/TableConfig.types';
import type { StoppedConditionMonitorState } from '@/vantage/types/VantageWorkbook.types';
import { randomUUID } from '@/utilities/utilities';

export const fetchEvidenceTableDefinition = async (id: string): Promise<void> => {
  try {
    const { data: evidenceTableDefinition } = await sqTableDefinitionsApi.getTableDefinition({ id });
    flux.dispatch('SET_EVIDENCE_TABLE_DEFINITION', { evidenceTableDefinition });
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const setExtensions = (extensions: ExtensionSchema[]) => {
  try {
    flux.dispatch(
      'SET_EXTENSIONS',
      extensions.filter(({ enabled }) => enabled),
    );
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
};

export const setSelectedNodes = (selectedNodes: SimplifiedNode[]) => {
  flux.dispatch('SET_SELECTED_NODES', { selectedNodes });
};

export const setActiveExtensions = (activeExtensions: string[]) => {
  flux.dispatch('SET_ACTIVE_EXTENSIONS', { activeExtensions });
};

export const markContextModified = () => {
  flux.dispatch('SET_CONTEXT_LAST_MODIFIED', { contextLastModified: Date.now() });
};

export const setConditionMonitorJobPercentComplete = (percentComplete: number | undefined) => {
  flux.dispatch('SET_CONDITION_MONITOR_JOB_PERCENT_COMPLETE', { percentComplete });
};

export const setConditionMonitorJobId = (conditionMonitorJobId: string | undefined) => {
  flux.dispatch('SET_CONDITION_MONITOR_JOB_ID', { conditionMonitorJobId });
};

export const setTableColumns = (tableColumns: TableColumns) => {
  flux.dispatch('SET_TABLE_COLUMNS', { tableColumns });
};

export const setEvidenceTableDefinition = (evidenceTableDefinition: TableDefinitionOutputV1) => {
  flux.dispatch('SET_EVIDENCE_TABLE_DEFINITION', { evidenceTableDefinition });
};

const setBooleanProperty = async (workbookId: string, propertyName: string, value: boolean) => {
  await sqItemsApi.setProperty(
    { value: String(value) },
    {
      id: workbookId,
      propertyName,
    },
  );
};

export const setIncludeExtinct = async (workbookId: string, includeExtinct: boolean) => {
  await setBooleanProperty(workbookId, 'includeExtinct', includeExtinct);
  flux.dispatch('SET_INCLUDE_EXTINCT', { includeExtinct });
};

export const setIsManualEventsEnabled = async (workbookId: string, isManualEventsEnabled: boolean): Promise<void> => {
  await setBooleanProperty(workbookId, 'isManualEventsEnabled', isManualEventsEnabled);
  flux.dispatch('SET_IS_MANUAL_EVENTS_ENABLED', { isManualEventsEnabled });
};

export const setStoppedConditionMonitorState = (
  stoppedConditionMonitorState: StoppedConditionMonitorState | undefined,
) => {
  flux.dispatch('SET_CONDITION_MONITOR_STOPPED_STATE', { stoppedConditionMonitorState });
};

/**
 * Update the key that forces a hard refresh of the evidence table on the next fetch.
 */
export const hardRefreshEvidenceTableOnNextFetch = () => {
  flux.dispatch('SET_HARD_EVIDENCE_TABLE_REFRESH_KEY', { hardEvidenceTableRefreshKey: randomUUID() });
};
